import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

interface OrderItem {
  id: string;
  productId: string;
  productName: string;
  color: string;
  size: string;
  quantity: number;
  price: number;
  image?: string;
}

interface Order {
  id: string;
  orderNumber: string;
  createdBy: string;
  createdByName: string;
  createdByRole: 'Manager' | 'Partner';
  customerName: string;
  customerPhone: string;
  customerAddress: string;
  notes: string;
  items: OrderItem[];
  totalAmount: number;
  status: 'pending' | 'processing' | 'shipping' | 'delivered' | 'returned' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

interface Product {
  id: string;
  name: string;
  category: string;
  variants: ProductVariant[];
  image: string;
  productionPrice: number;
  sellingPrice: number;
}

interface ProductVariant {
  id: string;
  color: string;
  size: string;
  stock: number;
  image?: string;
}

interface OrderFormProps {
  order?: Order;
  onSubmit: (orderData: Partial<Order>) => void;
  onCancel: () => void;
  currentUser: {
    id: string;
    name: string;
    role: 'Manager' | 'Partner';
    commissionRate: number;
  };
  isEditing?: boolean;
}

export const OrderForm = ({
  order,
  onSubmit,
  onCancel,
  currentUser,
  isEditing = false,
}: OrderFormProps) => {
  const [step, setStep] = useState(1);
  const [selectedItems, setSelectedItems] = useState<OrderItem[]>(order?.items || []);
  const [customerInfo, setCustomerInfo] = useState({
    customerName: order?.customerName || '',
    customerPhone: order?.customerPhone || '',
    customerAddress: order?.customerAddress || '',
    notes: order?.notes || '',
  });
  const [showProductSelector, setShowProductSelector] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Load products from Supabase
  const loadProducts = async () => {
    try {
      setLoadingProducts(true);

      // Load products
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (productsError) {
        console.error('Error loading products:', productsError);
        return;
      }

      // Load product variants
      const { data: variantsData, error: variantsError } = await supabase
        .from('product_variants')
        .select('*');

      if (variantsError) {
        console.error('Error loading variants:', variantsError);
      }

      // Transform data to match local format
      const transformedProducts: Product[] = (productsData || []).map(product => {
        const productVariants = (variantsData || []).filter(v => v.product_id === product.id);
        
        // Transform variants
        const variants: ProductVariant[] = productVariants.map(v => ({
          id: v.id,
          size: v.size,
          color: v.color,
          stock: v.stock_quantity,
          image: v.image_url || product.image_url
        }));

        return {
          id: product.id,
          name: product.name,
          category: product.category || 'Sản phẩm',
          productionPrice: product.base_price * 0.6, // Assuming 60% of base price
          sellingPrice: product.base_price,
          image: product.image_url || '',
          variants
        };
      });

      setProducts(transformedProducts);
    } catch (err) {
      console.error('Error in loadProducts:', err);
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const getDiscountedPrice = (sellingPrice: number) => {
    return (sellingPrice * (100 - currentUser.commissionRate)) / 100;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const addProductToOrder = (product: Product, variant: ProductVariant, quantity: number) => {
    const price =
      currentUser.role === 'Manager' ? product.sellingPrice : getDiscountedPrice(product.sellingPrice);

    const newItem: OrderItem = {
      id: Date.now().toString(),
      productId: product.id,
      productName: product.name,
      color: variant.color,
      size: variant.size,
      quantity,
      price,
      image: variant.image || product.image,
    };

    setSelectedItems([...selectedItems, newItem]);
    setShowProductSelector(false);
  };

  const removeItem = (itemId: string) => {
    setSelectedItems(selectedItems.filter((item) => item.id !== itemId));
  };

  const updateItemQuantity = (itemId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(itemId);
      return;
    }

    setSelectedItems(
      selectedItems.map((item) => (item.id === itemId ? { ...item, quantity } : item)),
    );
  };

  const getTotalAmount = () => {
    return selectedItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const handleSubmit = () => {
    if (selectedItems.length === 0) {
      alert('Vui lòng thêm ít nhất một sản phẩm vào đơn hàng');
      return;
    }

    if (!customerInfo.customerName || !customerInfo.customerPhone || !customerInfo.customerAddress) {
      alert('Vui lòng điền đầy đủ thông tin khách hàng');
      return;
    }

    const orderData: Partial<Order> = {
      ...customerInfo,
      items: selectedItems,
      totalAmount: getTotalAmount(),
    };

    onSubmit(orderData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-full max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
            {isEditing ? 'Chỉnh sửa đơn hàng' : 'Tạo đơn hàng mới'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Bước 1: Chọn sản phẩm */}
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                Bước 1: Chọn sản phẩm
              </h3>
              <button
                onClick={() => setShowProductSelector(true)}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 whitespace-nowrap shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                <i className="ri-add-line"></i>
                Thêm sản phẩm
              </button>
            </div>

            {selectedItems.length === 0 ? (
              <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-lg">
                <i className="ri-shopping-cart-line text-3xl sm:text-4xl text-gray-300 mb-4"></i>
                <p className="text-gray-500">Chưa có sản phẩm nào được chọn</p>
                <p className="text-sm text-gray-400 mt-1">Nhấn "Thêm sản phẩm" để bắt đầu</p>
              </div>
            ) : (
              <div className="space-y-4">
                {selectedItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex flex-col sm:flex-row items-start sm:items-center gap-4 p-4 border border-gray-200 rounded-lg bg-white hover:shadow-md transition-shadow"
                  >
                    <img
                      src={item.image}
                      alt={item.productName}
                      className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 truncate">{item.productName}</h4>
                      <p className="text-sm text-gray-600">
                        Màu: {item.color} | Size: {item.size}
                      </p>
                      <p className="text-sm font-medium text-emerald-600">{formatCurrency(item.price)}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => updateItemQuantity(item.id, item.quantity - 1)}
                        className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        <i className="ri-subtract-line text-sm"></i>
                      </button>
                      <span className="w-12 text-center font-medium">{item.quantity}</span>
                      <button
                        onClick={() => updateItemQuantity(item.id, item.quantity + 1)}
                        className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        <i className="ri-add-line text-sm"></i>
                      </button>
                    </div>
                    <div className="text-left sm:text-right flex-shrink-0">
                      <p className="font-semibold text-gray-900">
                        {formatCurrency(item.price * item.quantity)}
                      </p>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                    >
                      <i className="ri-delete-bin-line"></i>
                    </button>
                  </div>
                ))}

                <div className="flex justify-end pt-4 border-t border-gray-200">
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Tổng cộng:</p>
                    <p className="text-xl sm:text-2xl font-bold text-emerald-600">
                      {formatCurrency(getTotalAmount())}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bước 2: Thông tin khách hàng */}
          <div className="p-4 sm:p-6 border-t border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-6">
              Bước 2: Thông tin khách hàng
            </h3>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên khách hàng *
                </label>
                <input
                  type="text"
                  value={customerInfo.customerName}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, customerName: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
                  placeholder="Nhập tên khách hàng"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Số điện thoại *
                </label>
                <input
                  type="tel"
                  value={customerInfo.customerPhone}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, customerPhone: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base"
                  placeholder="Nhập số điện thoại"
                />
              </div>

              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Địa chỉ giao hàng *
                </label>
                <textarea
                  value={customerInfo.customerAddress}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, customerAddress: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base resize-none"
                  placeholder="Nhập địa chỉ giao hàng đầy đủ"
                />
              </div>

              <div className="lg:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ghi chú
                </label>
                <textarea
                  value={customerInfo.notes}
                  onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent text-base resize-none"
                  placeholder="Ghi chú thêm cho đơn hàng (tùy chọn)"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="p-4 sm:p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <span className="text-gray-600 text-sm sm:text-base">Tổng đơn hàng: </span>
              <span className="font-bold text-emerald-600 text-lg sm:text-xl">{formatCurrency(getTotalAmount())}</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
              <button
                onClick={onCancel}
                className="w-full sm:w-auto px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap font-medium"
              >
                Hủy bỏ
              </button>
              <button
                onClick={handleSubmit}
                className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white rounded-lg font-medium transition-all duration-200 whitespace-nowrap shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                {isEditing ? 'Cập nhật đơn hàng' : 'Hoàn thành đơn hàng'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Product Selector Modal */}
      {showProductSelector && (
        <ProductSelector
          products={products}
          onSelect={addProductToOrder}
          onClose={() => setShowProductSelector(false)}
          currentUser={currentUser}
          loading={loadingProducts}
        />
      )}
    </div>
  );
};

// Component chọn sản phẩm
interface ProductSelectorProps {
  products: Product[];
  onSelect: (product: Product, variant: ProductVariant, quantity: number) => void;
  onClose: () => void;
  currentUser: {
    id: string;
    name: string;
    role: 'Manager' | 'Partner';
    commissionRate: number;
  };
  loading: boolean;
}

const ProductSelector = ({
  products,
  onSelect,
  onClose,
  currentUser,
  loading,
}: ProductSelectorProps) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [quantity, setQuantity] = useState(1);

  const getDiscountedPrice = (sellingPrice: number) => {
    return sellingPrice * (100 - currentUser.commissionRate) / 100;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
    }).format(amount);
  };

  const handleSelect = () => {
    if (selectedProduct && selectedVariant) {
      onSelect(selectedProduct, selectedVariant, quantity);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[80vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <h3 className="text-xl font-bold text-gray-900">Chọn sản phẩm</h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Đang tải sản phẩm...</p>
              </div>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <i className="ri-product-hunt-line text-4xl mb-4"></i>
              <p>Không có sản phẩm nào</p>
              <p className="text-sm mt-2">Vui lòng thêm sản phẩm trước khi tạo đơn hàng</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Danh sách sản phẩm */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-4">Chọn sản phẩm</h4>
                <div className="space-y-3">
                  {products.map((product) => (
                    <div
                      key={product.id}
                      onClick={() => {
                        setSelectedProduct(product);
                        setSelectedVariant(null);
                      }}
                      className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                        selectedProduct?.id === product.id
                          ? 'border-emerald-500 bg-emerald-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <img src={product.image} alt={product.name} className="w-12 h-12 object-cover rounded-lg" />
                        <div className="flex-1">
                          <h5 className="font-medium text-gray-900">{product.name}</h5>
                          <p className="text-sm text-gray-600">{product.category}</p>
                          <p className="text-sm font-medium text-emerald-600">
                            {currentUser.role === 'Manager'
                              ? formatCurrency(product.sellingPrice)
                              : formatCurrency(getDiscountedPrice(product.sellingPrice))}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Chi tiết sản phẩm và variant */}
              <div>
                {selectedProduct ? (
                  <>
                    <h4 className="font-semibold text-gray-900 mb-4">Chọn màu sắc và kích thước</h4>
                    {selectedProduct.variants.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        <i className="ri-alert-line text-3xl mb-2"></i>
                        <p>Sản phẩm này chưa có biến thể</p>
                      </div>
                    ) : (
                      <>
                        <div className="space-y-3 mb-6">
                          {selectedProduct.variants.map((variant) => (
                            <div
                              key={variant.id}
                              onClick={() => setSelectedVariant(variant)}
                              className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                selectedVariant?.id === variant.id
                                  ? 'border-emerald-500 bg-emerald-50'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-gray-900">
                                    {variant.color} - {variant.size}
                                  </p>
                                  <p className="text-sm text-gray-600">Tồn kho: {variant.stock} sản phẩm</p>
                                </div>
                                {variant.image && (
                                  <img
                                    src={variant.image}
                                    alt={`${selectedProduct.name} ${variant.color}`}
                                    className="w-10 h-10 object-cover rounded"
                                  />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>

                        {selectedVariant && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Số lượng</label>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                  className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                                >
                                  <i className="ri-subtract-line text-sm"></i>
                                </button>
                                <input
                                  type="number"
                                  value={quantity}
                                  onChange={(e) =>
                                    setQuantity(Math.max(1, parseInt(e.target.value) || 1))
                                  }
                                  min="1"
                                  max={selectedVariant.stock}
                                  className="w-20 text-center border border-gray-300 rounded-lg py-1"
                                />
                                <button
                                  onClick={() => setQuantity(Math.min(selectedVariant.stock, quantity + 1))}
                                  className="w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                                >
                                  <i className="ri-add-line text-sm"></i>
                                </button>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Tối đa: {selectedVariant.stock} sản phẩm
                              </p>
                            </div>

                            <div className="p-4 bg-gray-50 rounded-lg">
                              <div className="flex justify-between items-center">
                                <span className="text-gray-600">Thành tiền:</span>
                                <span className="text-lg font-bold text-emerald-600">
                                  {formatCurrency(
                                    (currentUser.role === 'Manager'
                                      ? selectedProduct.sellingPrice
                                      : getDiscountedPrice(selectedProduct.sellingPrice)) *
                                      quantity,
                                  )}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <i className="ri-product-hunt-line text-4xl mb-4"></i>
                    <p>Chọn một sản phẩm để xem chi tiết</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 bg-gray-50 flex-shrink-0">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
            >
              Hủy bỏ
            </button>
            <button
              onClick={handleSelect}
              disabled={!selectedProduct || !selectedVariant}
              className="px-6 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors whitespace-nowrap"
            >
              Thêm vào đơn hàng
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};